/* eslint-disable no-console */
import { getApiHost } from '@hotjar/common';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUser } from '@hotjar/webapp/domains/acquisition/register/api';
import { getRoute } from '@hotjar/webapp/utils/routing';
import type { RegistrationDto } from '@hotjar/webapp/domains/acquisition/register/dto';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import {
  usePopulateStateParams,
  useRegisterQueryParams,
} from '@hotjar/webapp/features/Registration/hooks/useRegisterQueryParams';
import { useRegistrationCampaign } from '@hotjar/webapp/hooks/useCampaigns';
import { useAnonymousId } from '@hotjar/webapp/hooks/useAnonymousId';
import { useDeviceType } from '@hotjar/webapp/hooks/useDeviceType';
import { googleAnalytics } from '@hotjar/tracking/src/';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { ONBOARDING } from '@hotjar/webapp/constants/routes/onboarding';
import {
  DEFAULT_PRICING_TABLE_NUMBER,
  DEFAULT_SSO_PROVIDER,
  REGISTRATION_STEP,
  SSO_WINDOW_TARGET,
} from '@hotjar/webapp/features/Registration/common/constants';
import { BASIC } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import { getCurrentUser } from '@hotjar/user';
import { redirectLoginOtp, redirectMfaSetup } from '@hotjar/auth';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';
import { refreshFromServer } from '@hotjar/webapp/domains/common/user-data';
import { validateNextURL } from '@hotjar/webapp/features/Mfa/getNextURL';

export const useGoogleSsoSignUp = (
  ignoreMessageHandler?: boolean,
): {
  googleSsoSignUpClick: () => void;
  authenticate: (params: Record<string, string>) => void;
  registerData?: RegistrationDto;
} => {
  const SSO_ENDPOINT = `${getApiHost()}/api/sso/google-start`;
  const [registerData, setRegisterData] = useState<RegistrationDto>();

  const track = useTrack();
  const queryParams = useRegisterQueryParams();
  const { data: campaign } = useRegistrationCampaign(queryParams.campaignId);
  const { anonymousId } = useAnonymousId();
  const { deviceType } = useDeviceType();
  const signupParamSettings = usePopulateStateParams({ signupMethod: DEFAULT_SSO_PROVIDER });
  const nextUrl = useQueryParameter('next');
  const { i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0] ?? i18n.language ?? 'en';

  const handleSignUpMessage = useCallback(
    (data: RegistrationDto) => {
      if (ignoreMessageHandler) {
        return;
      }
      createUser({
        name: data.name,
        email: data.email,
        ssoProvider: data.provider,
        picture: data.picture,
        ssoToken: data.sso_token,
        terms: 'true',
        persistentAnonymousId: anonymousId,
        campaignHash: campaign?.hash,
        plan: (campaign?.valid && campaign.plan) || BASIC,
        pricingTable:
          (queryParams.pricingTable && Number(queryParams.pricingTable)) ||
          DEFAULT_PRICING_TABLE_NUMBER,
        locale: currentLocale,
      })
        .then(refreshFromServer)
        .then(() => {
          track.signupStepCompleted({
            signupMethod: data.provider,
            signupStepNumber: 1,
            campaign,
            signupStep: REGISTRATION_STEP,
            deviceType,
          });
          browserHistory.replace(getRoute(ONBOARDING.path, signupParamSettings));
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaign, queryParams, track, anonymousId],
  );

  const handleLoginSuccessRedirect = useCallback(() => {
    if (nextUrl && (nextUrl.indexOf('download') > -1 || nextUrl.indexOf('/r?') > -1)) {
      window.location.href = validateNextURL(nextUrl);
      return;
    }

    // Navigate to root path copying the current query params
    window.location.assign(`/${window.location.search}`);
  }, [nextUrl]);

  const handleLoginMessage = useCallback(async () => {
    const currentUser = await getCurrentUser();

    if (!currentUser) {
      track.tagRecording('Login Failed');
    }

    track.login();

    handleLoginSuccessRedirect();
  }, [track, handleLoginSuccessRedirect]);

  const onMessage = useCallback(
    (event: MessageEvent) => {
      const action = event.data?.action;
      const data = event.data?.sso_data;

      if (action && data) {
        switch (action) {
          case 'signup':
            setRegisterData(data);
            handleSignUpMessage(data);
            break;
          case 'login':
            // this means a user with this email already exists, so we've logged them in
            // we can now load the overview
            handleLoginMessage();
            break;
          case 'mfa_login':
            redirectLoginOtp(data.state, nextUrl);
            break;
          case 'partial_login_enforce_mfa':
            redirectMfaSetup(nextUrl);
            break;
          default:
            console.warn(`Unrecognized SSO action: ${action}`);
            break;
        }
      }
    },
    [handleSignUpMessage, handleLoginMessage, nextUrl],
  );

  const getEndpointUrl = (params: Record<string, string> = {}) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const queryString = new URLSearchParams({ ...params }).toString();
    if (queryString) {
      return [SSO_ENDPOINT, queryString].join('?');
    }
    return SSO_ENDPOINT;
  };

  const authenticate = (params: Record<string, string>) => {
    googleAnalytics.trackSignUpWithGoogle();
    track.click('Google Single-sign-on', 'Registration Step 1', {});
    track.tagRecording(SSO_WINDOW_TARGET);
    window.open(getEndpointUrl(params), SSO_WINDOW_TARGET, 'width=640,height=700');
  };

  const googleSsoClick = () => {
    authenticate(queryParams as Record<string, string>);
    track.signupStepSubmitted({
      signupMethod: DEFAULT_SSO_PROVIDER,
      signupStepNumber: 1,
      campaign,
      signupStep: REGISTRATION_STEP,
      deviceType,
    });
  };

  useEffect(() => {
    window.addEventListener('message', onMessage, false);
    return () => {
      window.removeEventListener('message', onMessage, false);
    };
    // Empty dep array in order to execute once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    registerData,
    authenticate,
    googleSsoSignUpClick: googleSsoClick,
  };
};
